import { HbLoadingSpinner, useModal } from "@homebound/beam";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router";
import useQueryString from "src/hooks/useQueryString";
import {
  DesignPackageConfiguratorSlotFragment,
  useDesignPackageItemSlotEditorQuery,
} from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageItemSlotEditor.generated";
import {
  DesignPackageConfigurator_DesignPackageFragment,
  useDesignPackageConfiguratorQuery,
} from "src/routes/libraries/design-catalog/design-package-configurator/DesignPackageConfigurator.generated";
import { createDesignPackageUrl } from "src/RouteUrls";
import { fail } from "src/utils";
import { DesignUpdatesAvailableModal } from "./components/DesignUpdatesAvailableButton";

export type DesignPackageConfiguratorContextState = {
  /** Basic metadata about the loaded DesignPackage */
  designPackage: DesignPackageConfigurator_DesignPackageFragment;
  /** The slots LefNav has selected for "main content" display in the ItemSlotEditor */
  selectedSlots: DesignPackageConfiguratorSlotFragment[] | undefined;
  /** The selected Options via DesignWithFilters */
  selectedOptionIds: string[];
  /** The topic of interest in the New>Product Search>Detail flow */
  materialVariantId: DesignPackageConfiguratorQueryString["materialVariantId"] | undefined;
  setCtxState: (partial: Partial<DesignPackageConfiguratorQueryString>) => void;
  /** The latest RPAV is either in draft, or can be used to create the next draft. */
  readOnly: boolean;
  loading: boolean;
};

export const DesignPackageConfiguratorContext = createContext<DesignPackageConfiguratorContextState>({
  designPackage: undefined!,
  setCtxState() {},
  selectedOptionIds: [],
  selectedSlots: undefined,
  loading: false,
  materialVariantId: undefined,
  readOnly: true,
});

export function DesignPackageConfiguratorProvider({ children }: React.PropsWithChildren<unknown>) {
  const { designPackageId, designPackageVersionId } = useParams<{
    designPackageId: string;
    designPackageVersionId: string;
  }>();
  const [qs, setQs] = useQueryString<DesignPackageConfiguratorQueryString>({
    optionIds: [],
    slotIds: [],
    acceptUpdates: undefined,
  });
  const { openModal } = useModal();

  // getting new arrays on each render cycle, so stabilize them
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slotIds = useMemo(() => qs.slotIds, [qs.slotIds.toString()]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const optionIds = useMemo(() => qs.optionIds, [qs.optionIds.toString()]);

  /** This is the Background data for all pages */
  const designCatalogQuery = useDesignPackageConfiguratorQuery({
    variables: { id: designPackageId, versionId: designPackageVersionId },
  });

  /** This is the "Center Panel" data that "Left Nav" is Setting */
  const selectedSlotQuery = useDesignPackageItemSlotEditorQuery({
    variables: { dpId: designPackageId, slotIds, rpavId: designPackageVersionId, optionIds },
    skip: !designPackageId || !designPackageVersionId || (slotIds.isEmpty && optionIds.isEmpty),
  });

  useEffect(() => {
    const dp = designCatalogQuery.data?.designPackage;
    if (qs.acceptUpdates && dp?.availableUpdates.nonEmpty) {
      openModal({
        content: <DesignUpdatesAvailableModal updates={dp.availableUpdates} designPackageId={dp.id} />,
      });
      setQs({ acceptUpdates: undefined });
    }
  }, [designCatalogQuery.data?.designPackage, openModal, qs.acceptUpdates, setQs]);

  // If the sync is running keep polling the DP
  useEffect(() => {
    designCatalogQuery.data?.designPackage.copyInProgress
      ? designCatalogQuery.startPolling(2_500)
      : designCatalogQuery.stopPolling();
  }, [designCatalogQuery]);

  /** backfill missing rpav:4547 id if it's missing from Route (someone navigated here with "latest" which became undefined and never got backfilled) */
  const history = useHistory();
  const loadedVersionId = designCatalogQuery.data?.designPackage.aggregateVersions.last?.id;
  if (!designPackageVersionId && loadedVersionId) {
    history.replace(createDesignPackageUrl(designPackageId, loadedVersionId));
  }

  return (
    <DesignPackageConfiguratorContext.Provider
      value={{
        designPackage:
          designCatalogQuery.data?.designPackage! ?? (!designCatalogQuery.loading && fail("No design package")),
        setCtxState: setQs,
        selectedOptionIds: optionIds,
        materialVariantId: qs.materialVariantId,
        selectedSlots:
          // If user had options selected (DWF), then de-selected the last option, return `[]` to return back to "nothing selected; please pick a slot/filters" screen
          (slotIds.isEmpty && optionIds.isEmpty && []) ||
          // prevent flicker when switching between slots as `data` clears out when new variables are set before new data is returned
          (selectedSlotQuery.data?.designPackage.slots ?? selectedSlotQuery.previousData?.designPackage.slots),
        loading: designCatalogQuery.loading || selectedSlotQuery.loading,
        // The latest RPAV is either in draft, or can be used to create the next draft.
        readOnly: designPackageVersionId !== designCatalogQuery.data?.designPackage.aggregateVersions.last?.id,
      }}
    >
      {designCatalogQuery.loading ? <HbLoadingSpinner /> : children}
    </DesignPackageConfiguratorContext.Provider>
  );
}

/**
 * Several pages within DesignPackage are trying to leverage QueryString including:
 * 1. Context: selected Slots from leftnav
 * 2. SlotEditor: selectedOptionIds for DesignWithFilters
 * 3. DesignPackageProductCardPage: materialVariantId (possible "noMaterialVariant" for isDisabledMaterialVariant)
 */
type DesignPackageConfiguratorQueryString = {
  /** Set by LeftNav to tell ItemSlotEditor which Slots to load */
  slotIds: string[];
  /** Used by DesignWithFilters. Propagates to DesignPackageProductCardPage so the selected filters auto-apply to new TLIs */
  optionIds: string[];
  /**
   * The selected MaterialVariant for the DesignPackageProductCardPage, either from the selected Card or forwarded from the
   * ProductSearchPage and about to be committed to the card. Pass in `"noMaterialVariant"` to show the "No Material Variant" UI.
   */
  materialVariantId: `mv:${number}` | "noMaterialVariant";
  /** Automatically opens a modal to accept incoming plan updates*/
  acceptUpdates: boolean | undefined;
};

export const useDesignPackageConfiguratorContext = () => useContext(DesignPackageConfiguratorContext);
